import React from 'react'

import {
  AuthError,
  AuthProvider,
  getAdditionalUserInfo,
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth'
import { useForm } from 'react-hook-form'
import { Link as ReactRouterLink } from 'react-router-dom'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Link,
  Spinner,
  Text,
} from '@chakra-ui/react'

import { GoogleIcon } from '@/assets/icons'
import { useAuth } from '@/context/AuthContext/AuthContext'
import { googleProvider } from '@/firebase'
import { useCreateUser } from '@/react-query/user'
import { useDocumentTitle } from '@/views/hooks/useDocumentTitle'

const GoogleSignIn = () => {
  const createUser = useCreateUser()

  const signInWithProvider = (
    provider: AuthProvider,
  ): React.MouseEventHandler<HTMLButtonElement> => {
    return async (event) => {
      event.preventDefault()
      try {
        const res = await signInWithPopup(getAuth(), provider)
        const additionalInfo = getAdditionalUserInfo(res)
        if (additionalInfo?.isNewUser) {
          await createUser.mutateAsync()
        }
      } catch (err) {
        console.error(err)
      }
    }
  }

  return (
    <Button
      className="mt-3 w-full"
      leftIcon={<GoogleIcon w={6} h={5} />}
      variant={'brand-solid'}
      onClick={signInWithProvider(googleProvider)}
    >
      Sign in with Google
    </Button>
  )
}

const OrContinueWith = () => {
  return (
    <div className="mt-6 flex">
      <span className="inset-0 flex-grow flex items-center">
        <div className="w-full border-t border-[#00000033]" />
      </span>
      <div className="relative flex justify-center text-md leading-5 text-black">
        <span className="px-2">Or continue with</span>
      </div>
      <span className="inset-0 flex-grow flex items-center">
        <div className="w-full border-t border-[#00000033]" />
      </span>
    </div>
  )
}

interface ISignInForm {
  email: string
  password: string
}

const EmailPasswordSignIn = () => {
  const {
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
    register,
  } = useForm<ISignInForm>()

  const handleEmailPasswordSignIn = async ({
    email,
    password,
  }: ISignInForm) => {
    try {
      await signInWithEmailAndPassword(getAuth(), email, password)
    } catch (err) {
      if ((err as AuthError).code === 'auth/user-not-found') {
        setError('email', {
          type: 'wrongEmail',
          message: 'User with this email does not exist',
        })
        return
      }
      if ((err as AuthError).code === 'auth/wrong-password') {
        setError('password', {
          type: 'wrongPassword',
          message: 'Password is incorrect',
        })
        return
      }
      console.error(err)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleEmailPasswordSignIn)}>
      <FormControl mt={5} isInvalid={Boolean(errors.email)}>
        <FormLabel color={'black'} fontWeight={'bold'} htmlFor="email">
          Email
        </FormLabel>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          {...register('email', {
            required: 'Email is required',
          })}
          variant={'brand-solid-white'}
        />
        <FormErrorMessage>
          {errors.email && errors.email.message}
        </FormErrorMessage>
      </FormControl>
      <FormControl mt={3} isInvalid={Boolean(errors.password)}>
        <FormLabel color={'black'} fontWeight={'bold'} htmlFor="password">
          Password
        </FormLabel>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          {...register('password', {
            required: 'Password is required',
          })}
          variant={'brand-solid-white'}
        />
        <FormErrorMessage>
          {errors.password && errors.password.message}
        </FormErrorMessage>
      </FormControl>
      <Link
        as={ReactRouterLink}
        fontSize={'md'}
        color="#6B5CE6"
        fontWeight={700}
        to="/forgot-password"
        className="mt-2 block"
      >
        Forgot password?
      </Link>
      <Button
        className="mt-6 w-full"
        isLoading={isSubmitting}
        type="submit"
        variant="brand-solid"
        colorScheme="blue"
        py={'0.3rem'}
      >
        Sign in
      </Button>
    </form>
  )
}

const SignIn = () => {
  useDocumentTitle('Sign in')

  const { isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex flex-grow">
        <Spinner className="m-auto w-10 h-10" />
      </div>
    )
  }

  return (
    <main className="w-full">
      <Heading size={'lg'} className="text-center" color={'black'}>
        Sign in
      </Heading>
      <div className="mt-2 text-center">
        <Text color={'black'}>
          or{' '}
          <Link
            as={ReactRouterLink}
            color="#6B5CE6"
            className="font-semibold"
            to="/sign-up"
          >
            create an account
          </Link>
        </Text>
      </div>
      <Box className="w-full flex flex-col mt-5">
        <GoogleSignIn />
        <OrContinueWith />
        <EmailPasswordSignIn />
      </Box>
    </main>
  )
}

export default SignIn
